import React from 'react';
import { Text } from 'braid-design-system';
import { sanitizeNumericString } from '../../utils/numericInput';

interface CardPaymentDraft {
  cardTypeCode: string;
  stationNo: string;
  approvalCode: string;
  cardNumber: string;
  amount: string;
}

interface CardPaymentConfig {
  paycode: string;
  paystno?: string;
  payDispno?: number;
}

interface CardPaymentModalProps {
  isOpen: boolean;
  selectedPaymentModeLabel: string;
  selectedPaymode: string;
  outstandingAmount: number;
  selectedCardPaymentConfig: CardPaymentConfig | null;
  cardPaymentDraft: CardPaymentDraft;
  cardPaymentConfigs: CardPaymentConfig[];
  onClose: () => void;
  onDraftChange: (field: keyof CardPaymentDraft, value: string) => void;
  onApply: () => void;
}

const CardPaymentModal: React.FC<CardPaymentModalProps> = ({
  isOpen,
  selectedPaymentModeLabel,
  selectedPaymode,
  outstandingAmount,
  selectedCardPaymentConfig,
  cardPaymentDraft,
  cardPaymentConfigs,
  onClose,
  onDraftChange,
  onApply,
}) => {
  if (!isOpen) {
    return null;
  }

  return (
    <div className="search-modal-overlay">
      <div className="card-payment-modal">
        <div className="search-modal-header">
          <div>
            <Text weight="strong" size="large">
              Card Payment
            </Text>
            <Text tone="secondary" size="small">
              Capture terminal proof before completing the invoice
            </Text>
          </div>
          <button type="button" onClick={onClose} className="search-modal-close">
            x
          </button>
        </div>

        <div className="card-payment-modal-content">
          <div className="card-payment-summary">
            <div className="card-payment-summary-row">
              <span>Mode</span>
              <strong>{selectedPaymentModeLabel || selectedPaymode}</strong>
            </div>
            <div className="card-payment-summary-row">
              <span>Outstanding</span>
              <strong>{outstandingAmount.toFixed(2)}</strong>
            </div>
            <div className="card-payment-summary-row">
              <span>Valid Prefix</span>
              <strong>{selectedCardPaymentConfig?.paystno || '-'}</strong>
            </div>
          </div>

          <div className="card-payment-form-grid">
            <label className="card-payment-field">
              <span>Card Type</span>
              <select
                value={cardPaymentDraft.cardTypeCode}
                onChange={(event) => onDraftChange('cardTypeCode', event.target.value)}
                className="billing-selector-search-input"
              >
                <option value="">Select card type</option>
                {cardPaymentConfigs.map((config) => (
                  <option key={config.paycode} value={config.paycode}>
                    {config.paycode}
                  </option>
                ))}
              </select>
            </label>
            <label className="card-payment-field">
              <span>Terminal No</span>
              <input
                type="text"
                value={cardPaymentDraft.stationNo}
                onChange={(event) => onDraftChange('stationNo', event.target.value)}
                placeholder="T001"
                readOnly
                className="billing-selector-search-input"
              />
            </label>
            <label className="card-payment-field">
              <span>Approval Code</span>
              <input
                type="text"
                value={cardPaymentDraft.approvalCode}
                onChange={(event) => onDraftChange('approvalCode', event.target.value)}
                placeholder="AUTH1234"
                readOnly
                className="billing-selector-search-input"
              />
            </label>
            <label className="card-payment-field">
              <span>Card Number</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={selectedCardPaymentConfig?.payDispno ? (selectedCardPaymentConfig.payDispno === 5 ? 5 : 4) : 4}
                value={cardPaymentDraft.cardNumber}
                onChange={(event) =>
                    onDraftChange(
                        'cardNumber',
                        sanitizeNumericString(event.target.value, {
                            mode: 'integer',
                            maxLength: selectedCardPaymentConfig?.payDispno === 5 ? 5 : 4,
                        }),
                    )
                }
                placeholder={selectedCardPaymentConfig ? '0'.repeat(selectedCardPaymentConfig.payDispno === 5 ? 5 : 4) : '0000'}
                className="billing-selector-search-input"
              />
              <small className="card-payment-help-text">
                Enter the last {selectedCardPaymentConfig?.payDispno === 5 ? 5 : 4} digits.
              </small>
            </label>
            <label className="card-payment-field">
              <span>Amount</span>
              <input
                type="text"
                inputMode="decimal"
                value={cardPaymentDraft.amount}
                onChange={(event) => onDraftChange('amount', sanitizeNumericString(event.target.value))}
                placeholder="0.00"
                className="billing-selector-search-input"
              />
            </label>
          </div>

          <div className="card-payment-modal-actions">
            <button type="button" className="billing-bottom-btn clear" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="billing-bottom-btn charge" onClick={onApply}>
              Apply Card Payment
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CardPaymentModal;
